import {
  clampSfxVolume,
  formatSfxVolumeLabel,
  getStoredSfxVolume,
  setStoredSfxVolume,
  SFX_VOLUME_MAX,
  SFX_VOLUME_MIN,
} from "@dawn/adapters";
import {
  getGameStrings,
  getLanguageLabel,
  getSiteStrings,
  SUPPORTED_LOCALES,
  type LocaleId,
} from "@dawn/i18n";
import {
  useEscapeToDismiss,
  useFocusOnOpen,
  useLandingModalFocusTrap,
  useLandingModalState,
} from "@site/modals/landingModalHelpers";
import { useLocale } from "@site/LocaleContext";
import { useEffect, useRef, useState, type RefObject } from "react";
import styles from "@site/modals/LandingSettingsModal.module.css";

interface LandingSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  returnFocusRef: RefObject<HTMLElement | null>;
  restoreFocus?: boolean;
}

function sfxVolumeLabelFor(locale: LocaleId, level: number): string {
  return formatSfxVolumeLabel(getGameStrings(locale).sfxVolumeLabel, level);
}

export function LandingSettingsModal({
  isOpen,
  onClose,
  returnFocusRef,
  restoreFocus = true,
}: LandingSettingsModalProps) {
  const { locale, setLocale } = useLocale();
  const [sfxVolume, setSfxVolume] = useState(() => getStoredSfxVolume());
  const languageSelectRef = useRef<HTMLSelectElement>(null);

  const { panelRef } = useLandingModalState(isOpen, returnFocusRef, restoreFocus);

  const game = getGameStrings(locale);
  const site = getSiteStrings(locale);
  const sfxVolumeLabel = sfxVolumeLabelFor(locale, sfxVolume);

  useEffect(() => {
    if (isOpen) {
      setSfxVolume(getStoredSfxVolume());
    }
  }, [isOpen]);

  useFocusOnOpen(isOpen, languageSelectRef);
  useLandingModalFocusTrap(panelRef, isOpen);
  useEscapeToDismiss(isOpen, onClose);

  const handleLanguageChange = (next: LocaleId): void => {
    if (!SUPPORTED_LOCALES.includes(next)) {
      return;
    }

    setLocale(next);
  };

  const handleSfxVolumeInput = (rawLevel: number): void => {
    const level = clampSfxVolume(rawLevel);
    setStoredSfxVolume(level);
    setSfxVolume(level);
  };

  return (
    <div
      className={styles.modal}
      data-testid="landing-settings-modal"
      hidden={!isOpen}
    >
      <div
        className={styles.backdrop}
        data-testid="landing-settings-backdrop"
        onClick={onClose}
      />
      <div
        ref={panelRef}
        className={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby="landing-settings-title"
        data-testid="landing-settings-panel"
      >
        <button
          className={styles.close}
          type="button"
          data-testid="landing-settings-close"
          aria-label={site.landing.settingsCloseAriaLabel}
          onClick={onClose}
        >
          {site.landing.chronicleModalCloseLabel}
        </button>
        <h2
          id="landing-settings-title"
          className={styles.title}
          data-testid="landing-settings-title"
        >
          {game.settingsTitle}
        </h2>
        <label
          className={styles.field}
          data-testid="landing-settings-language-field"
        >
          <span
            className={styles.label}
            data-testid="landing-settings-language-label"
          >
            {game.languageLabel}
          </span>
          <select
            ref={languageSelectRef}
            className={styles.select}
            data-testid="landing-settings-language"
            value={locale}
            onChange={(event) => {
              handleLanguageChange(event.target.value as LocaleId);
            }}
          >
            {SUPPORTED_LOCALES.map((id) => (
              <option key={id} value={id}>
                {getLanguageLabel(locale, id)}
              </option>
            ))}
          </select>
        </label>
        <label
          className={styles.field}
          data-testid="landing-settings-sfx-volume-field"
        >
          <span
            className={styles.label}
            data-testid="landing-settings-sfx-volume-label"
          >
            {sfxVolumeLabel}
          </span>
          <input
            className={styles.slider}
            type="range"
            min={SFX_VOLUME_MIN}
            max={SFX_VOLUME_MAX}
            step={1}
            value={sfxVolume}
            data-testid="landing-settings-sfx-volume"
            aria-label={sfxVolumeLabel}
            onInput={(event) => {
              handleSfxVolumeInput(Number(event.currentTarget.value));
            }}
          />
        </label>
      </div>
    </div>
  );
}
